<template>
  <div class="countdown-timer">
    <div class="time-unit" v-for="unit in timeUnits" :key="unit.label">
      <div class="flip-card-group d-flex">
        <div class="flip-card" v-for="digit in unit.digits" :key="digit.label">
          <div class="flip-card-inner">
            <div class="flip-card-top" :class="{ flipping: digit.flipping }">
              <div class="flip-card-front">
                <span class="top">{{ digit.current }}</span>
              </div>
              <div class="flip-card-back">
                <span class="top">{{ digit.next }}</span>
              </div>
            </div>
            <div class="flip-card-bottom">
              <div class="flip-card-front">
                <span class="bottom">{{ digit.current }}</span>
              </div>
              <div class="flip-card-back">
                <span class="bottom">{{ digit.next }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="countdown-timer_label">{{ unit.label }}</div>
    </div>
    <div class="countdown-timer_content">{{ content }}</div>
    <div v-if="isTimeUp" class="countdown-timer_completion-message">{{ completionMessage }}</div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';

const props = defineProps({
  endTime: {
    type: String,
    required: true,
  },
  currentTime: {
    type: String,
    required: true,
  },
  content: {
    type: String,
    required: true,
  },
  completionMessage: {
    type: String,
    required: true,
  },
});

const timeUnits = ref([
  { label: 'Days', digits: [{ label: 'd1', current: 0, next: 0, flipping: false }, { label: 'd2', current: 0, next: 0, flipping: false }] },
  { label: 'Hours', digits: [{ label: 'h1', current: 0, next: 0, flipping: false }, { label: 'h2', current: 0, next: 0, flipping: false }] },
  { label: 'Minutes', digits: [{ label: 'm1', current: 0, next: 0, flipping: false }, { label: 'm2', current: 0, next: 0, flipping: false }] },
  { label: 'Seconds', digits: [{ label: 's1', current: 0, next: 0, flipping: false }, { label: 's2', current: 0, next: 0, flipping: false }] },
]);

let timer = null;
const isTimeUp = ref(false);

const startCountdown = () => {
  updateTimeUnits();

  timer = setInterval(() => {
    updateTimeUnits();
  }, 1000);
};

const updateTimeUnits = () => {
  const now = new Date().getTime();
  const serverTime = new Date(props.currentTime).getTime();
  const distance = new Date(props.endTime).getTime() - (serverTime + (now - serverTime));

  if (distance < 0) {
    clearInterval(timer);
    timeUnits.value.forEach(unit => {
      unit.digits.forEach(digit => {
        digit.current = 0;
        digit.next = 0;
      });
    });
    isTimeUp.value = true;
    return;
  }

  isTimeUp.value = false;

  const days = Math.floor(distance / (1000 * 60 * 60 * 24));
  const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((distance % (1000 * 60)) / 1000);

  const timeArray = [
    padTime(days, 2),
    padTime(hours, 2),
    padTime(minutes, 2),
    padTime(seconds, 2),
  ];

  timeUnits.value.forEach((unit, index) => {
    unit.digits.forEach((digit, digitIndex) => {
      const newDigitValue = parseInt(timeArray[index].charAt(digitIndex));
      digit.next = newDigitValue;
      if (digit.current !== digit.next) {
        digit.flipping = true;
        setTimeout(() => {
          digit.current = digit.next;
          digit.flipping = false;
        }, 600);
      }
    });
  });
};

const padTime = (time, length) => {
  return time.toString().padStart(length, '0');
};

onMounted(startCountdown);
onUnmounted(() => clearInterval(timer));

watch(() => props.endTime, startCountdown);
watch(() => props.currentTime, startCountdown);
</script>
